import { z } from 'zod'
import { parseDataOfType } from '@/common/parseDataType'
import { apiErrorHandler } from '../errorHandler'

export const PresentationStatus = {
  Drafting: 'drafting',
  OutlineReady: 'outline_ready',
  SlidesDrafted: 'slides_drafted',
  Ready: 'ready',
  Exported: 'exported'
} as const

export const PresentationLayout = {
  TitleAndContent: 'title_and_content',
  TwoColumn: 'two_column',
  SectionHeader: 'section_header'
} as const

export const PresentationSlideState = {
  Outline: 'outline',
  Drafted: 'drafted',
  Ready: 'ready'
} as const

export const PresentationMessageAuthor = {
  User: 'user',
  Assistant: 'assistant'
} as const

export const PresentationFitStatus = {
  Comfortable: 'comfortable',
  Tight: 'tight',
  Overflow: 'overflow'
} as const

export const PresentationFitIssue = {
  TitleTooLong: 'title_too_long',
  KeyMessageTooLong: 'key_message_too_long',
  TooManyPoints: 'too_many_points',
  PointTooLong: 'point_too_long',
  BodyOverflow: 'body_overflow',
  LeftColumnOverflow: 'left_column_overflow',
  RightColumnOverflow: 'right_column_overflow'
} as const
export type PresentationFitIssue = (typeof PresentationFitIssue)[keyof typeof PresentationFitIssue]

export const PresentationFitResultSchema = z.object({
  status: z.enum(PresentationFitStatus),
  usedUnits: z.number().int().nonnegative(),
  capacityUnits: z.number().int().positive(),
  issues: z.array(z.enum(PresentationFitIssue))
})
export type PresentationFitResult = z.infer<typeof PresentationFitResultSchema>

export const PresentationFitLayoutPolicySchema = z.object({
  titleCharsPerLine: z.number().int().positive(),
  maxTitleLines: z.number().int().positive(),
  bodyCharsPerLine: z.number().int().positive(),
  maxBodyLines: z.number().int().positive(),
  maxKeyMessageLines: z.number().int().positive(),
  maxPointLines: z.number().int().positive(),
  maxPoints: z.number().int().positive()
})

export const PresentationFitPolicySchema = z.object({
  version: z.number().int().positive(),
  comfortablePercent: z.number().int().min(1).max(100),
  layouts: z.object({
    title_and_content: PresentationFitLayoutPolicySchema,
    two_column: PresentationFitLayoutPolicySchema,
    section_header: PresentationFitLayoutPolicySchema
  })
})
export type PresentationFitPolicy = z.infer<typeof PresentationFitPolicySchema>

export const PresentationSlideSchema = z.object({
  slideId: z.number(),
  ordinal: z.number(),
  title: z.string(),
  keyMessage: z.string(),
  contentPoints: z.array(z.string()),
  layout: z.enum(PresentationLayout),
  visualDirection: z.string(),
  speakerNotes: z.string(),
  sourceArtifactIds: z.array(z.number()).default([]),
  sourceDocIds: z.array(z.number()),
  state: z.enum(PresentationSlideState),
  selectedVersionId: z.number().nullable(),
  fit: PresentationFitResultSchema
})
export type PresentationSlide = z.infer<typeof PresentationSlideSchema>

export const PresentationSourceArtifactSchema = z.object({
  sourceArtifactId: z.number(),
  artifactType: z.literal('proposal'),
  artifactId: z.number(),
  artifactTitle: z.string(),
  sourceUpdatedAtTsMs: z.number(),
  capturedOnTsMs: z.number(),
  originAvailable: z.boolean()
})
export type PresentationSourceArtifact = z.infer<typeof PresentationSourceArtifactSchema>

export const PresentationSchema = z.object({
  presentationId: z.number(),
  projectId: z.number(),
  title: z.string(),
  purpose: z.string(),
  audience: z.string(),
  targetSlideCount: z.number(),
  themeDocId: z.number().nullable(),
  status: z.enum(PresentationStatus),
  createdOnTsMs: z.number(),
  updatedAtTsMs: z.number(),
  slides: z.array(PresentationSlideSchema),
  sourceArtifacts: z.array(PresentationSourceArtifactSchema).default([]),
  fitPolicy: PresentationFitPolicySchema
})
export type Presentation = z.infer<typeof PresentationSchema>

export const ListPresentationsResponseSchema = z.object({ presentations: z.array(PresentationSchema) })

export const PresentationSourceSchema = z.object({ docId: z.number(), docType: z.number(), fileName: z.string() })
export const ListPresentationSourcesResponseSchema = z.object({ sources: z.array(PresentationSourceSchema) })

export const CreatePresentationRequestSchema = z.object({
  title: z.string().trim().min(1, 'Title is required'),
  purpose: z.string().trim().min(1, 'Purpose is required'),
  audience: z.string().trim().min(1, 'Audience is required'),
  targetSlideCount: z.number().int().min(3).max(30),
  themeDocId: z.number().optional(),
  sourceDocIds: z.array(z.number()).default([])
})
export type CreatePresentationRequest = z.input<typeof CreatePresentationRequestSchema>

export const CreatePresentationFromProposalRequestSchema = CreatePresentationRequestSchema.extend({
  sourceProposalId: z.number().int().positive()
})
export type CreatePresentationFromProposalRequest = z.input<typeof CreatePresentationFromProposalRequestSchema>

export const RenamePresentationRequestSchema = z.object({ title: z.string().trim().min(1, 'Title is required') })

export const UpdatePresentationSlideRequestSchema = PresentationSlideSchema.omit({
  slideId: true,
  ordinal: true,
  selectedVersionId: true,
  fit: true
}).extend({ sourceArtifactIds: z.array(z.number()) })
export type UpdatePresentationSlideRequest = z.input<typeof UpdatePresentationSlideRequestSchema>

export const PresentationSlideVersionSchema = z.object({
  versionId: z.number(),
  slideId: z.number(),
  title: z.string(),
  keyMessage: z.string(),
  contentPoints: z.array(z.string()),
  layout: z.enum(PresentationLayout),
  visualDirection: z.string(),
  speakerNotes: z.string(),
  sourceArtifactIds: z.array(z.number()).default([]),
  sourceDocIds: z.array(z.number()),
  createdOnTsMs: z.number(),
  fit: PresentationFitResultSchema
})
export type PresentationSlideVersion = z.infer<typeof PresentationSlideVersionSchema>

export const PresentationSlideMessageSchema = z.object({
  messageId: z.number(),
  slideId: z.number(),
  author: z.enum(PresentationMessageAuthor),
  content: z.string(),
  version: PresentationSlideVersionSchema.nullable(),
  createdOnTsMs: z.number()
})
export type PresentationSlideMessage = z.infer<typeof PresentationSlideMessageSchema>

export const PresentationMessageSchema = z.object({
  messageId: z.number(),
  presentationId: z.number(),
  author: z.enum(PresentationMessageAuthor),
  content: z.string(),
  createdOnTsMs: z.number()
})
export type PresentationMessage = z.infer<typeof PresentationMessageSchema>

export const ListPresentationSlideMessagesResponseSchema = z.object({
  messages: z.array(PresentationSlideMessageSchema)
})
export const ListPresentationMessagesResponseSchema = z.object({ messages: z.array(PresentationMessageSchema) })
export const RefinePresentationResponseSchema = z.object({
  presentation: PresentationSchema,
  message: PresentationMessageSchema
})
export const RefinePresentationRequestSchema = z.object({ instruction: z.string().trim().min(1) })
export const SelectPresentationSlideVersionRequestSchema = z.object({ versionId: z.number().int().positive() })

export const ExportPresentationResponseSchema = z.object({ downloadUrl: z.string(), fileName: z.string() })

async function presentationJSON<T>(response: Response, schema: z.ZodType<T>): Promise<T> {
  await apiErrorHandler(response)
  const data: unknown = await response.json()
  return parseDataOfType(data, schema)
}

export async function listPresentations(projectId: number): Promise<Array<Presentation>> {
  const response = await fetch(`/api/projects/${projectId}/presentations`, {
    method: 'GET',
    credentials: 'same-origin'
  })
  return (await presentationJSON(response, ListPresentationsResponseSchema)).presentations
}

export async function getPresentation(projectId: number, presentationId: number): Promise<Presentation> {
  const response = await fetch(`/api/projects/${projectId}/presentations/${presentationId}`, {
    method: 'GET',
    credentials: 'same-origin'
  })
  return presentationJSON(response, PresentationSchema)
}

export async function createPresentation(projectId: number, request: CreatePresentationRequest): Promise<Presentation> {
  const response = await fetch(`/api/projects/${projectId}/presentations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(CreatePresentationRequestSchema.parse(request))
  })
  return presentationJSON(response, PresentationSchema)
}

export async function createPresentationFromProposal(
  projectId: number,
  request: CreatePresentationFromProposalRequest
): Promise<Presentation> {
  const response = await fetch(`/api/projects/${projectId}/presentations/from-proposal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(CreatePresentationFromProposalRequestSchema.parse(request))
  })
  return presentationJSON(response, PresentationSchema)
}

export async function deletePresentation(projectId: number, presentationId: number): Promise<void> {
  const response = await fetch(`/api/projects/${projectId}/presentations/${presentationId}`, {
    method: 'DELETE',
    credentials: 'same-origin'
  })
  await apiErrorHandler(response)
}

export async function renamePresentation(
  projectId: number,
  presentationId: number,
  title: string
): Promise<Presentation> {
  const response = await fetch(`/api/projects/${projectId}/presentations/${presentationId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(RenamePresentationRequestSchema.parse({ title }))
  })
  return presentationJSON(response, PresentationSchema)
}

export async function generatePresentationOutline(projectId: number, presentationId: number): Promise<Presentation> {
  const response = await fetch(`/api/projects/${projectId}/presentations/${presentationId}/outline`, {
    method: 'POST',
    credentials: 'same-origin'
  })
  return presentationJSON(response, PresentationSchema)
}

export async function buildPresentationSlides(projectId: number, presentationId: number): Promise<Presentation> {
  const response = await fetch(`/api/projects/${projectId}/presentations/${presentationId}/build`, {
    method: 'POST',
    credentials: 'same-origin'
  })
  return presentationJSON(response, PresentationSchema)
}

export async function updatePresentationSlide(
  projectId: number,
  presentationId: number,
  slideId: number,
  request: UpdatePresentationSlideRequest
): Promise<PresentationSlide> {
  const response = await fetch(`/api/projects/${projectId}/presentations/${presentationId}/slides/${slideId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(UpdatePresentationSlideRequestSchema.parse(request))
  })
  return presentationJSON(response, PresentationSlideSchema)
}

export async function exportPresentation(
  projectId: number,
  presentationId: number
): Promise<z.infer<typeof ExportPresentationResponseSchema>> {
  const response = await fetch(`/api/projects/${projectId}/presentations/${presentationId}/export`, {
    method: 'POST',
    credentials: 'same-origin'
  })
  return presentationJSON(response, ExportPresentationResponseSchema)
}

export async function getPresentationSlideMessages(
  projectId: number,
  presentationId: number,
  slideId: number
): Promise<Array<PresentationSlideMessage>> {
  const response = await fetch(
    `/api/projects/${projectId}/presentations/${presentationId}/slides/${slideId}/messages`,
    { credentials: 'same-origin' }
  )
  return (await presentationJSON(response, ListPresentationSlideMessagesResponseSchema)).messages
}

export async function refinePresentationSlide(
  projectId: number,
  presentationId: number,
  slideId: number,
  instruction: string
): Promise<PresentationSlideMessage> {
  const response = await fetch(`/api/projects/${projectId}/presentations/${presentationId}/slides/${slideId}/refine`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(RefinePresentationRequestSchema.parse({ instruction }))
  })
  return presentationJSON(response, PresentationSlideMessageSchema)
}

export async function selectPresentationSlideVersion(
  projectId: number,
  presentationId: number,
  slideId: number,
  versionId: number
): Promise<PresentationSlide> {
  const response = await fetch(`/api/projects/${projectId}/presentations/${presentationId}/slides/${slideId}/select`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(SelectPresentationSlideVersionRequestSchema.parse({ versionId }))
  })
  return presentationJSON(response, PresentationSlideSchema)
}

export async function deselectPresentationSlideVersion(
  projectId: number,
  presentationId: number,
  slideId: number
): Promise<PresentationSlide> {
  const response = await fetch(
    `/api/projects/${projectId}/presentations/${presentationId}/slides/${slideId}/deselect`,
    { method: 'POST', credentials: 'same-origin' }
  )
  return presentationJSON(response, PresentationSlideSchema)
}

export async function getPresentationMessages(
  projectId: number,
  presentationId: number
): Promise<Array<PresentationMessage>> {
  const response = await fetch(`/api/projects/${projectId}/presentations/${presentationId}/messages`, {
    credentials: 'same-origin'
  })
  return (await presentationJSON(response, ListPresentationMessagesResponseSchema)).messages
}

export async function refinePresentationDeck(
  projectId: number,
  presentationId: number,
  instruction: string
): Promise<z.infer<typeof RefinePresentationResponseSchema>> {
  const response = await fetch(`/api/projects/${projectId}/presentations/${presentationId}/refine`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(RefinePresentationRequestSchema.parse({ instruction }))
  })
  return presentationJSON(response, RefinePresentationResponseSchema)
}
